import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Miniheader from '../../../../components/layout/MiniHeader/index'
import Pagination from '../../../../components/common/Pagination/Pagination'
import Button from '../../../../components/common/Button/index'
import GridTable from '../../../../components/common/GridTable/GridTable'
import { FaCirclePlus } from 'react-icons/fa6'
import Typography from '../../../../components/common/Typography'
import Backdrop from '../../../../components/common/Backdrop/Backdrop'
import SurveyForm from '../../../../components/forms/Survey Form/SurveyForm'
import { MdOutlineVerified } from 'react-icons/md'
import { ListItemButton, ListItemAvatar, Avatar, Stack, List, ListItemText } from '@mui/material'
import Rating from '@mui/material/Rating'
import LoadingIndicator from '../../../../components/state/LoadingIndicator'
import ErrorState from '../../../../components/state/ErrorState'
import {
  fetchAllReviews,
  markReviewAsOpened
} from '../../../../state/redux/actions/reviewActions'
import {
  setReviewFilters,
  clearReviewFilters
} from '../../../../state/redux/slices/reviewSlice'

const columns = [
  { colname: 'Review', colSpan: 'col-span-4', colStyle: 'flex justify-start' },
  { colname: 'Rating', colSpan: 'col-span-1' },
  { colname: 'Broker', colSpan: 'col-span-1' },
  { colname: 'Property', colSpan: 'col-span-1' },
]

const GeneralReview = () => {
  const dispatch = useDispatch()
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [formOpen, setFormOpen] = useState(false)
  const [toastOpen, setToastOpen] = useState(false)
  const [selectedReview, setSelectedReview] = useState(null)
  const [showDetailModal, setShowDetailModal] = useState(false)

  // Redux state
  const {
    allReviews,
    filteredReviews,
    filters,
    loading,
    error
  } = useSelector(state => state.reviews)

  // Fetch reviews on component mount
  useEffect(() => {
    dispatch(fetchAllReviews())
  }, [dispatch])

  // Handle the correct payload structure: payload.data.reviews
  const safeAllReviews = Array.isArray(allReviews?.data?.reviews)
    ? allReviews.data.reviews
    : Array.isArray(allReviews)
      ? allReviews
      : []

  const safeFilteredReviews = Array.isArray(filteredReviews?.data?.reviews)
    ? filteredReviews.data.reviews
    : Array.isArray(filteredReviews)
      ? filteredReviews
      : []

  const displayReviews = safeFilteredReviews.length > 0 || Object.values(filters).some(f => f)
    ? safeFilteredReviews
    : safeAllReviews

  // Get pagination data
  const paginationData = allReviews?.data?.pagination || {
    currentPage: 1,
    totalPages: 1,
    totalCount: displayReviews.length,
    hasNext: false,
    hasPrev: false
  }

  // Handle filter change from MiniHeader
  const handleMiniHeaderFilterChange = (filtered, dateRange) => {
    if (!dateRange) {
      // No date filter applied - clear filters
      dispatch(clearReviewFilters())
      setCurrentPage(1)
      return
    }

    // Date filter is applied - set filtering state
    dispatch(setReviewFilters({ dateRange }))
    setCurrentPage(1) // Reset to first page when filtering
  }

  const handleRefresh = () => {
    setCurrentPage(1)
    dispatch(fetchAllReviews())
  }

  // Handle review actions

  const handleMarkAsOpened = async (reviewId) => {
    try {
      await dispatch(markReviewAsOpened(reviewId)).unwrap()
    } catch (error) {
      console.error('Failed to mark review as opened:', error)
    }
  }



  const handleViewReviewDetails = (review) => {
    setSelectedReview(review)
    setShowDetailModal(true)
    handleMarkAsOpened(review._id)
  }

  // Pagination logic - using API pagination if available
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedReviews = displayReviews.slice(startIndex, endIndex)

  if (loading && safeAllReviews.length === 0) {
    return <LoadingIndicator />
  }

  if (error) {
    return <ErrorState error={error} />
  }

  return (
    <>
      <Miniheader
        title="Reviews"
        data={safeAllReviews}
        onFilterChange={handleMiniHeaderFilterChange}
      />
      <div className="ml-[250px] p-8 max-w-7xl">
        <div>
          <div className="flex py-2 justify-between items-center">
            <Pagination
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalItems={paginationData.totalCount || displayReviews.length}
              onPageChange={setCurrentPage}
              onRefresh={handleRefresh}
              justify="start"
            />
            <div className="flex gap-2">
              <Button onClick={() => setFormOpen(prev => !prev)}>
                <FaCirclePlus className="text-xl mr-2" />
                <Typography>Fill Survey</Typography>
              </Button>
            </div>
          </div>
          
          {formOpen && (
            <Backdrop isOpen={formOpen} onClose={() => setFormOpen(false)}>
              <SurveyForm onSubmit={() => setToastOpen(true)} onClose={() => setFormOpen(false)} />
            </Backdrop>
          )}
          
          {toastOpen && (
            <Backdrop isOpen={toastOpen} onClose={() => setToastOpen(false)}>
              <div className="bg-white p-6 rounded-lg flex flex-col items-center justify-center">
                <MdOutlineVerified className="text-primary text-4xl mb-4" />
                <Typography variant="h4" className="mb-4">Review Submitted Successfully!</Typography>
                <Button onClick={() => setToastOpen(false)}>Done</Button>
              </div>
            </Backdrop>
          )}
          

          
          <div>
            <GridTable columns={columns} gridCols="grid-cols-7">
              {paginatedReviews.length > 0 ? (
                paginatedReviews.map((review) => (
                  <ListItemButton
                    key={review._id}
                    component="div"
                    className="col-span-7 hover:bg-gray-50 border-b border-gray-100"
                    onClick={() => handleViewReviewDetails(review)}
                  >
                    <div className="grid grid-cols-7 gap-6 w-full py-4">
                      <div className="col-span-4">
                        <div className="flex gap-3">
                          <ListItemAvatar>
                            <Avatar 
                              src={review.userId?.avatar || ''} 
                              alt={review.userId?.full_name}
                              className="w-10 h-10"
                            >
                              {review.userId?.firstName?.charAt(0) || 'U'}
                            </Avatar>
                          </ListItemAvatar>
                          <div className="flex flex-col flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Typography variant="h4" className="font-medium text-gray-900">
                                {review.userId?.full_name || 'Anonymous'}
                              </Typography>
                              <Typography variant="caption" className="text-gray-500">
                                {new Date(review.createdAt).toLocaleDateString('en-US', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </Typography>
                            </div>
                            <Typography variant="body" className="text-gray-700 text-sm leading-relaxed">
                              {review.comment || review.title || 'Quality and received property at good price with top-notch customer service.'}
                            </Typography>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center col-span-1 h-full">
                        <div className="flex items-center gap-1">
                          <Stack spacing={1}>
                            <Rating
                              name="half-rating-read"
                              value={review.rating || 4}
                              precision={0.5}
                              readOnly
                              size="small"
                              sx={{
                                '& .MuiRating-iconFilled': {
                                  color: '#f59e0b',
                                },
                              }}
                            />
                          </Stack>
                        </div>
                      </div>

                      <div className="flex items-center col-span-1 h-full">
                        <Typography variant="body" className="text-gray-700 text-sm">
                          {review.agentId?.full_name || review.broker || 'Ambers Raj'}
                        </Typography>
                      </div>

                      <div className="flex items-center col-span-1 h-full">
                        <Typography variant="body" className="text-gray-700 text-sm truncate">
                          {review.propertyId?.property_title || review.property || 'Erik apartment'}
                        </Typography>
                      </div>
                    </div>
                  </ListItemButton>
                ))
              ) : (
                <div className="col-span-7 text-center py-8">
                  <Typography variant="body" className="text-gray-500">
                    {loading ? 'Loading reviews...' : 'No reviews found'}
                  </Typography>
                </div>
              )}
            </GridTable>
          </div>

          {/* Review Detail Modal */}
          {showDetailModal && selectedReview && (
            <Backdrop isOpen={showDetailModal} onClose={() => setShowDetailModal(false)}>
              <div className="bg-white p-6 rounded-lg max-w-2xl w-full mx-4">
                <h2 className="text-xl font-semibold mb-4">Review Details</h2>
                <List>
                  <ListItemButton className="hover:bg-transparent">
                    <ListItemAvatar>
                      <Avatar src={selectedReview.userId?.avatar || ''} alt={selectedReview.userId?.full_name}>
                        {selectedReview.userId?.firstName?.charAt(0) || 'U'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={selectedReview.userId?.full_name || 'Anonymous'} 
                      secondary={new Date(selectedReview.createdAt).toLocaleDateString()} 
                    />
                  </ListItemButton>
                </List>
                <div className="flex flex-col space-y-4 mt-4">
                  <div>
                    <Typography variant="h4" className="font-semibold">Title:</Typography>
                    <Typography>{selectedReview.title || 'No title'}</Typography>
                  </div>
                  <div>
                    <Typography variant="h4" className="font-semibold">Comment:</Typography>
                    <Typography>{selectedReview.comment || 'No comment provided'}</Typography>
                  </div>
                  <div>
                    <Typography variant="h4" className="font-semibold">Rating:</Typography>
                    <Stack spacing={1}>
                      <Rating value={selectedReview.rating || 0} precision={0.5} readOnly />
                      <Typography>({selectedReview.rating}/5)</Typography>
                    </Stack>
                  </div>
                  <div>
                    <Typography variant="h4" className="font-semibold">Review Type:</Typography>
                    <Typography>{selectedReview.reviewType?.replace('_', ' ') || 'N/A'}</Typography>
                  </div>
                  {selectedReview.propertyId && (
                    <div>
                      <Typography variant="h4" className="font-semibold">Property:</Typography>
                      <Typography>{selectedReview.propertyId.property_title}</Typography>
                    </div>
                  )}
                  {selectedReview.agentId && (
                    <div>
                      <Typography variant="h4" className="font-semibold">Agent:</Typography>
                      <Typography>{selectedReview.agentId.full_name}</Typography>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Typography variant="h4" className="font-semibold">Status:</Typography>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      selectedReview.status === 'ACTIVE' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedReview.status}
                    </span>
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={() => setShowDetailModal(false)}>
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            </Backdrop>
          )}
        </div>
      </div>
    </>
  )
}

export default GeneralReview